import React from "react";
import { createRoot } from "react-dom/client";
import { ThemeProvider } from "../shared/context/ThemeContext.jsx";
import { loadAppConfig, getConfigDefaults } from "../api/configLoader.js";
import { isLoggedOutLocally } from "../api/client.js";
import { applyGlobalPrivacyControlDefaultIfNeeded } from "../shared/cookieConsent/cookieConsentStorage.js";
import { getApiBase } from "../shared/utils/urlHelpers.js";
import { registerPackageDropdownToggle } from "../shared/utils/packageDropdownToggle.js";
import { syncDocumentNativeSelectChromeClass } from "../shared/utils/nmNativeSelectLightChrome.js";
import { isMarketingDocumentPath } from "./marketingConstants.js";
import { bootstrapMarketingHeaderChromeFromLocation } from "./marketingHeaderChrome.js";
import { resolveAuthSuccessRedirectHref } from "./marketingGetLabelHandoff.js";

registerPackageDropdownToggle();
syncDocumentNativeSelectChromeClass();
document.documentElement.setAttribute("data-nm-resolved", "light");

const CONFIG_WAIT_MS = 6000;

(async function marketingBootstrap() {
  const container = document.getElementById("marketing-root");
  if (!container) return;

  bootstrapMarketingHeaderChromeFromLocation();

  const configPromise = loadAppConfig().catch((e) => {
    console.error("Marketing config load failed:", e);
    return null;
  });

  try {
    const timeoutPromise = new Promise((resolve) => setTimeout(() => resolve("timeout"), CONFIG_WAIT_MS));
    const raced = await Promise.race([configPromise, timeoutPromise]);
    // If the race timed out, still give config a short chance so logged-in redirects aren't flaky.
    if (raced === "timeout") {
      await Promise.race([
        configPromise,
        new Promise((resolve) => setTimeout(resolve, 1500)),
      ]);
    }
    if (!window.nitromuleReactData) {
      window.nitromuleReactData = {
        ...getConfigDefaults(),
        apiUrl: `${getApiBase()}${getApiBase().endsWith("/api") ? "" : "/api"}`
      };
    }
  } catch (e) {
    console.error("Marketing config load failed:", e);
    window.nitromuleReactData = window.nitromuleReactData || {
      ...getConfigDefaults(),
      apiUrl: `${window.location.origin}/api`
    };
  }

  if (!isMarketingDocumentPath(window.location.pathname)) {
    window.location.replace("/");
    return;
  }

  // Logged-in users may browse marketing pages. Only bounce off auth screens into the workspace.
  if (window.nitromuleReactData?.userLoggedIn && !isLoggedOutLocally()) {
    const path = String(window.location.pathname || "").split("?")[0].replace(/\/$/, "") || "/";
    if (path === "/login" || path === "/signup") {
      window.location.replace(resolveAuthSuccessRedirectHref(window.location.search));
      return;
    }
  }

  applyGlobalPrivacyControlDefaultIfNeeded();

  try {
    const { mountScaleToZeroWakeUp } = await import("../app/mountScaleToZeroWakeUp.js");
    await mountScaleToZeroWakeUp();
  } catch (e) {
    console.error("[nm] scale-to-zero wake mount failed:", e?.message || e);
  }

  try {
    const { default: MarketingApp } = await import("./MarketingApp.js");
    const { default: AppErrorBoundary } = await import("../app/AppErrorBoundary.js");
    const root = createRoot(container);
    root.render(
      React.createElement(
        React.StrictMode,
        null,
        React.createElement(
          ThemeProvider,
          { forceResolvedTheme: "light" },
          React.createElement(AppErrorBoundary, null, React.createElement(MarketingApp))
        )
      )
    );
  } catch (err) {
    console.error("Marketing bootstrap failed:", err);
    container.innerHTML = '<div style="padding:24px;font-family:Noto Sans,sans-serif;"><p style="color:#E9042B;">Marketing site failed to load.</p></div>';
  }
})().catch(err => {
  console.error("Marketing bootstrap failed:", err);
});
